import { supabase } from './auth.service';
import { Category } from '../entities/category.entity';
import { mapUser } from '../lib/map-user';

const CATEGORY_SELECT = '*, createdBy:users!created_by(*)';

export class CategoryService {
  constructor() {}

  async loadAll(): Promise<Category[]> {
    const { data, error } = await supabase
      .from('categories')
      .select(CATEGORY_SELECT)
      .order('sort_order', { ascending: true });

    if (error) throw error;
    return data.map(this.mapToEntity);
  }

  async loadByIds(ids: string[]): Promise<Category[]> {
    const { data, error } = await supabase.from('categories').select(CATEGORY_SELECT).in('id', ids);

    if (error) throw error;
    return data.map(this.mapToEntity);
  }

  async loadTree(): Promise<Category[]> {
    const { data, error } = await supabase
      .from('categories')
      .select(CATEGORY_SELECT)
      .order('sort_order', { ascending: true });

    if (error) throw error;

    const categories = data.map(this.mapToEntity);
    return this.buildTree(categories);
  }

  async loadById(id: string): Promise<Category> {
    const { data, error } = await supabase
      .from('categories')
      .select(CATEGORY_SELECT)
      .eq('id', id)
      .single();

    if (error) throw error;
    return this.mapToEntity(data);
  }

  async deleteById(id: string): Promise<void> {
    const { error } = await supabase.from('categories').delete().eq('id', id);

    if (error) throw error;
  }

  async create(category: Omit<Category, 'id' | 'children' | 'createdBy'>): Promise<Category> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('categories')
      .insert({
        account_id: category.accountId,
        name: category.name,
        description: category.description,
        media_id: category.media?.id ?? null,
        media_data: category.media ?? null,
        level: category.level,
        sort_order: category.sortOrder,
        parent_id: category.parentId,
        is_visible: true,
        created_by: user.id,
      })
      .select(CATEGORY_SELECT)
      .single();

    if (error) throw error;
    return this.mapToEntity(data);
  }

  async updateById(id: string, category: Partial<Category>): Promise<Category> {
    const updateData: any = {
      name: category.name,
      description: category.description,
      media_id: category.media?.id ?? null,
      media_data: category.media ?? null,
      level: category.level,
      sort_order: category.sortOrder,
      parent_id: category.parentId,
    };

    const { data, error } = await supabase
      .from('categories')
      .update(updateData)
      .eq('id', id)
      .select(CATEGORY_SELECT)
      .single();

    if (error) throw error;
    return this.mapToEntity(data);
  }

  async moveCategory(categoryId: string, newParentId?: string, index?: number): Promise<void> {
    const updateData: any = {
      parent_id: newParentId || null,
    };

    if (index !== undefined) {
      updateData.sort_order = index;

      const siblings = await supabase
        .from('categories')
        .select('id, sort_order')
        .eq('parent_id', newParentId || null)
        .gte('sort_order', index)
        .order('sort_order', { ascending: true });

      if (siblings.data && !siblings.error) {
        for (const sibling of siblings.data) {
          if (sibling.id !== categoryId) {
            await supabase
              .from('categories')
              .update({ sort_order: sibling.sort_order + 1 })
              .eq('id', sibling.id);
          }
        }
      }
    }

    const { error } = await supabase.from('categories').update(updateData).eq('id', categoryId);

    if (error) throw error;
  }

  private buildTree(categories: Category[], parentId: string | null = null): Category[] {
    return categories
      .filter((cat) => cat.parentId === parentId)
      .map((cat) => ({
        ...cat,
        children: this.buildTree(categories, cat.id),
      }));
  }

  private mapToEntity(row: any): Category {
    return {
      id: row.id,
      accountId: row.account_id,
      name: row.name,
      description: row.description,
      media: row.media_data || null,
      level: row.level,
      sortOrder: row.sort_order,
      path: row.path,
      parentId: row.parent_id,
      isVisible: row.is_visible,
      slug: row.slug,
      metaTitle: row.meta_title,
      metaDescription: row.meta_description,
      icon: row.icon,
      color: row.color,
      children: [],
      createdBy: mapUser(row.createdBy, row.account_id),
    };
  }
}

export const categoryService = new CategoryService();
